import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Button from '../components/Button.jsx';
import { FaClipboardList, FaUser, FaEnvelope, FaPhone, FaCalendarAlt, FaClock, FaMapMarkerAlt, FaPalette, FaCheckCircle, FaArrowRight, FaLightbulb, FaMagic } from 'react-icons/fa';
import '../styles/Book.css';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const ALL_TEMPLATES = [
  { id: 1, title: 'Classic Elegance', category: 'Wedding', price: '₱200' },
  { id: 2, title: 'Modern Romance', category: 'Wedding', price: '₱250' },
  { id: 3, title: 'Rustic Garden', category: 'Wedding', price: '₱300' },
  { id: 4, title: 'Kids Party Fun', category: 'Birthday', price: '₱200' },
  { id: 5, title: 'Elegant Milestone', category: 'Birthday', price: '₱250' },
  { id: 6, title: 'Fiesta Theme', category: 'Birthday', price: '₱200' },
  { id: 7, title: 'Angelic White', category: 'Christening', price: '₱200' },
  { id: 8, title: 'Blessed Journey', category: 'Christening', price: '₱250' },
  { id: 9, title: 'Professional Blue', category: 'Corporate', price: '₱200' },
  { id: 10, title: 'Executive Gold', category: 'Corporate', price: '₱300' },
  { id: 11, title: 'Silver Anniversary', category: 'Anniversary', price: '₱250' },
  { id: 12, title: 'Golden Years', category: 'Anniversary', price: '₱300' },
];

const Book = () => {
  const [searchParams] = useSearchParams();
  const pageTopRef = React.useRef(null);
  
  // Get template info from URL params
  const templateId = searchParams.get('template');
  const templateName = searchParams.get('name');
  const templateCategory = searchParams.get('category');
  const templatePrice = searchParams.get('price');
  const isCustomRequest = searchParams.get('type') === 'custom';

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    designType: isCustomRequest ? 'custom' : (templateId ? 'template' : ''),
    selectedTemplate: templateId ? `${templateName} (${templateCategory})` : '',
    eventType: templateCategory || '',
    eventDate: '',
    eventTime: '',
    venue: '',
    theme: '',
    colorPreference: '',
    additionalDetails: '',
    referenceDesign: '',
    howDidYouHear: '',
    agreedToTerms: false
  });

  // Update form when URL params change
  useEffect(() => {
    if (templateId && templateName) {
      setFormData(prev => ({
        ...prev,
        designType: 'template',
        selectedTemplate: `${templateName} (${templateCategory})`,
        eventType: templateCategory || prev.eventType
      }));
    } else if (isCustomRequest) {
      setFormData(prev => ({
        ...prev,
        designType: 'custom',
        selectedTemplate: ''
      }));
    }
  }, [templateId, templateName, templateCategory, isCustomRequest]);

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const getPriceRange = () => {
    if (formData.designType === 'custom') {
      return '₱350 - ₱700';
    } else if (formData.designType === 'template' && templatePrice) {
      return templatePrice;
    }
    return '₱200 - ₱700';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('')

    // Basic client-side validation (friendly messages)
    if (!formData.fullName || !formData.email || !formData.phone || !formData.eventType || !formData.eventDate) {
      setErrorMessage('Please provide your name, email, phone, event type and event date.')
      return
    }

    // Terms agreement check
    if (!formData.agreedToTerms) {
      setErrorMessage('Please agree to the Terms & Conditions before submitting.')
      return
    }

    // Philippine phone number validation (09XXXXXXXXX or +639XXXXXXXXX)
    const phPhoneRegex = /^(09|\+639)\d{9}$/
    if (!phPhoneRegex.test(formData.phone.replace(/\s|-/g, ''))) {
      setErrorMessage('Please enter a valid Philippine phone number (e.g. 09XX XXX XXXX).')
      return
    }

    // Event date must not be in the past
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const selectedDate = new Date(formData.eventDate)
    if (selectedDate < today) {
      setErrorMessage('Event date cannot be in the past. Please select a future date.')
      return
    }

    setLoading(true)
    try {
      const payload = {
        ...formData,
        templateId: templateId || null,
        templateName: templateName || formData.selectedTemplate || '',
        templateCategory: templateCategory || '',
        templatePrice: templatePrice || ''
      }

      const resp = await fetch(`${API_BASE_URL}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}))
        throw new Error(data.message || "Sorry, we couldn't send your request. Please try again later.")
      }

      setSubmitted(true)
      // Scroll to top so user sees the success message
      window.scrollTo({ top: 0, behavior: 'smooth' })
      if (pageTopRef.current) {
        pageTopRef.current.scrollIntoView({ behavior: 'smooth' })
      }
    } catch (err) {
      setErrorMessage(err.message || "Sorry, we couldn't send your request. Please try again later.")
    } finally {
      setLoading(false)
    }
  };

  if (submitted) {
    return (
      <div id="booknow-page" ref={pageTopRef}>
        <div className="success-message">
          <div className="success-icon"><FaCheckCircle /></div>
          <h2>Thank you — we received your booking request.</h2>
          <p>Our sales team will contact you within 24 hours to discuss your e-invitation and confirm pricing.</p>
          <div className="success-details">
            <p><strong>What happens next?</strong></p>
            <ul>
                <li>📱 <strong>Step 1:</strong> Our team contacts you via Messenger within 24 hours</li>
                <li>💳 <strong>Step 2:</strong> We send payment details — pay 50% downpayment to start</li>
                <li>🎨 <strong>Step 3:</strong> We create your e-invitation based on your preferences</li>
                <li>👀 <strong>Step 4:</strong> You review the design and request any revisions</li>
                <li>✅ <strong>Step 5:</strong> Once approved, pay the remaining 50% balance</li>
                <li>🎉 <strong>Step 6:</strong> Receive your final e-invitation — share the love!</li>
                <li>⭐ <strong>Step 7:</strong> Leave feedback via Messenger or tag us in your post</li>
            </ul>
          </div>
          <Button variant="primary" onClick={() => setSubmitted(false)}>
            Submit Another Booking
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div id="booknow-page">
      <div className="book-header">
        <h1>Book Your E-Invitation</h1>
        <p>Fill out the form below and we'll create your perfect digital invitation</p>
        {formData.designType === 'template' && templateName && (
          <div className="selected-template-badge">
            <span><FaClipboardList /> Selected Template:</span>
            <strong>{templateName}</strong>
            <span className="template-price">{templatePrice}</span>
          </div>
        )}
        {formData.designType === 'custom' && (
            <div className="selected-template-badge custom">
            <span><FaMagic /> Custom Design Request</span>
            <span className="template-price">₱350 - ₱700</span>
          </div>
        )}
        {!formData.designType && (
          <div className="price-badge">
            <span className="price-label">Price Range:</span>
            <span className="price-value">₱200 - ₱700</span>
          </div>
        )}
      </div>

      <div className="booknow-container">
        <form className="book-form" onSubmit={handleSubmit}>
          {errorMessage && (
            <div className="form-error" role="alert" style={{ color: '#b00020', marginBottom: '12px' }}>
              {errorMessage}
            </div>
          )}
          {/* Design Type Selection */}
          <div className="form-section">
            <h2><FaPalette /> Design Type</h2>
            
            <div className="design-type-options">
              <label className={`design-type-card ${formData.designType === 'template' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="designType"
                  value="template"
                  checked={formData.designType === 'template'}
                  onChange={handleChange}
                />
                <div className="design-type-content">
                  <span className="design-type-icon"><FaClipboardList /></span>
                  <span className="design-type-title">Use a Template</span>
                  <span className="design-type-price">₱200 - ₱300</span>
                  <span className="design-type-desc">Choose from our ready-made designs</span>
                </div>
              </label>
              
              <label className={`design-type-card ${formData.designType === 'custom' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="designType"
                  value="custom"
                  checked={formData.designType === 'custom'}
                  onChange={handleChange}
                />
                <div className="design-type-content">
                  <span className="design-type-icon"><FaMagic /></span>
                  <span className="design-type-title">Custom Design</span>
                  <span className="design-type-price">₱350 - ₱700</span>
                  <span className="design-type-desc">Unique design based on your vision</span>
                </div>
              </label>
            </div>

            {formData.designType === 'template' && (
              <div className="form-group" style={{ marginTop: '1.5rem' }}>
                <label htmlFor="selectedTemplate">Selected Template *</label>
                <select
                  id="selectedTemplate"
                  name="selectedTemplate"
                  value={formData.selectedTemplate}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a template</option>
                  {ALL_TEMPLATES.map((t) => (
                    <option key={t.id} value={`${t.title} (${t.category})`}>
                      {t.title} — {t.category}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Personal Information */}
          <div className="form-section">
            <h2><FaUser /> Your Information</h2>
            
            <div className="form-group">
              <label htmlFor="fullName">Full Name *</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Juan Dela Cruz"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="09XX XXX XXXX"
                  required
                />
              </div>
            </div>
          </div>

          {/* Event Details */}
          <div className="form-section">
            <h2><FaCalendarAlt /> Event Details</h2>
            
            <div className="form-group">
              <label htmlFor="eventType">Event Type *</label>
              <select
                id="eventType"
                name="eventType"
                value={formData.eventType}
                onChange={handleChange}
                required
              >
                <option value="">Select event type</option>
                <option value="wedding">Wedding</option>
                <option value="birthday">Birthday</option>
                <option value="christening">Christening / Baptism</option>
                <option value="anniversary">Anniversary</option>
                <option value="corporate">Corporate Event</option>
                <option value="debut">Debut (18th Birthday)</option>
                <option value="graduation">Graduation</option>
                <option value="baby-shower">Baby Shower</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="eventDate">Event Date *</label>
                <input
                  type="date"
                  id="eventDate"
                  name="eventDate"
                  value={formData.eventDate}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="eventTime">Event Time</label>
                <input
                  type="time"
                  id="eventTime"
                  name="eventTime"
                  value={formData.eventTime}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="venue">Event Venue / Location</label>
              <input
                type="text"
                id="venue"
                name="venue"
                value={formData.venue}
                onChange={handleChange}
                placeholder="e.g., Manila Hotel, BGC, etc."
              />
            </div>
          </div>

          {/* Design Preferences */}
          <div className="form-section">
            <h2><FaPalette /> Design Preferences</h2>
            
            <div className="form-group">
              <label htmlFor="theme">Theme / Motif</label>
              <input
                type="text"
                id="theme"
                name="theme"
                value={formData.theme}
                onChange={handleChange}
                placeholder="e.g., Rustic, Minimalist, Floral, Filipino, etc."
              />
            </div>

            <div className="form-group">
              <label htmlFor="colorPreference">Color Preference</label>
              <input
                type="text"
                id="colorPreference"
                name="colorPreference"
                value={formData.colorPreference}
                onChange={handleChange}
                placeholder="e.g., Blush pink and gold, Navy blue and silver"
              />
            </div>

            <div className="form-group">
              <label htmlFor="referenceDesign">Reference Design (Optional)</label>
              <input
                type="text"
                id="referenceDesign"
                name="referenceDesign"
                value={formData.referenceDesign}
                onChange={handleChange}
                placeholder="Paste a link to Pinterest, Instagram, or our sample you like"
              />
            </div>

            <div className="form-group">
              <label htmlFor="additionalDetails">Additional Details / Special Requests</label>
              <textarea
                id="additionalDetails"
                name="additionalDetails"
                value={formData.additionalDetails}
                onChange={handleChange}
                placeholder="Tell us more about your event, special requests, or any details you'd like included in the invitation..."
                rows="4"
              />
            </div>
          </div>

          {/* How did you hear about us */}
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="howDidYouHear">How did you hear about us?</label>
              <select
                id="howDidYouHear"
                name="howDidYouHear"
                value={formData.howDidYouHear}
                onChange={handleChange}
              >
                <option value="">Select an option</option>
                <option value="facebook">Facebook</option>
                <option value="instagram">Instagram</option>
                <option value="google">Google Search</option>
                <option value="referral">Friend/Family Referral</option>
                <option value="website">Logica Technology Website</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="form-actions">
            <div className="form-note">
              <p><FaLightbulb /> <strong>Note:</strong> After submitting, our team will contact you within 24 hours to discuss your design and confirm the final price.</p>
              <p style={{marginTop: '0.5rem'}}>💳 <strong>Payment Terms:</strong> 50% downpayment upon booking. Remaining 50% is due once the design is completed and approved by you.</p>
            </div>

            <label className="terms-checkbox-label">
              <input
                type="checkbox"
                name="agreedToTerms"
                checked={formData.agreedToTerms}
                onChange={(e) => setFormData(prev => ({ ...prev, agreedToTerms: e.target.checked }))}
                required
              />
              <span>
                I have read and agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Notice</a>.
              </span>
            </label>
            <Button type="submit" variant="primary" size="large" disabled={loading}>
              {loading ? 'Sending your booking request…' : 'Submit Booking Request'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Book;
